import { createSelector } from 'reselect';
// import state types
import {
    AppState,
    MinCostPerSquareFootVersionState,
 } from '@app/redux/states';
// import api models
import {
    MinCostPerSquareFootWithId,
    MinCostPerSquareFootFriendlyViewSummary,
    PossibleConstructionType,
} from '@app/models/api';
// Import selectors.
import {
    makeGetPossibleConstructionTypesCommercialSelector,
    makeGetPossibleConstructionTypesPersonalSelector,
} from '@app/redux/selectors';
// import base selector
import {
    getMinCostPerSquareFootVersionsState,
} from '@app/redux/selectors/base';
// import enumerations
import {
    BroadLineOfBusiness
} from '@app/enumerations';
// import props
import {
    MinCostPerSquareFootProps
} from '@app/props';

const getMinCostPerSquareFootVersionState = (state: AppState, props: MinCostPerSquareFootProps): MinCostPerSquareFootVersionState | undefined => {
    return getMinCostPerSquareFootVersionsState(state)?.minCostPerSquareFootVersionState[props.versionId];
};

// Get Min Costs Per Square Foot Selector.
const makeGetMinCostsPerSquareFootSelector = () => createSelector(
    [getMinCostPerSquareFootVersionState],
    (minCostPerSquareFootState: MinCostPerSquareFootVersionState | undefined): MinCostPerSquareFootWithId[] => {
        return minCostPerSquareFootState?.minCostsPerSquareFoot ?? [];
    }
);

//#region Friendly View Selectors
const makeGetMinCostPerSquareFootFriendlyViewPersonalSelector = () => createSelector(
        [
            makeGetMinCostsPerSquareFootSelector(),
            makeGetPossibleConstructionTypesPersonalSelector(),
        ],
        (
            minCostsPerSquareFoot: MinCostPerSquareFootWithId[],
            possibleConstructionTypesPersonal: PossibleConstructionType[]
        ): MinCostPerSquareFootFriendlyViewSummary[] => {
            const personalMinCosts: MinCostPerSquareFootFriendlyViewSummary[] = minCostsPerSquareFoot
                .filter((minCostPerSquareFoot: MinCostPerSquareFootWithId) =>
                        minCostPerSquareFoot.broadLineOfBusinessCode === BroadLineOfBusiness.PERSONAL
                        && minCostPerSquareFoot.constructionTypeCode
                        && minCostPerSquareFoot.stateCode)
                .map((minCostPerSquareFoot: MinCostPerSquareFootWithId) => {
                    const match: PossibleConstructionType | undefined = possibleConstructionTypesPersonal
                        .find((possibleConstructionType) => possibleConstructionType.code === minCostPerSquareFoot.constructionTypeCode);
                    return new MinCostPerSquareFootFriendlyViewSummary({
                        id: minCostPerSquareFoot.id ?? 0,
                        broadLineOfBusiness: 'Personal',
                        state: minCostPerSquareFoot.stateCode ?? String.empty(),
                        constructionType: match?.displayName ?? String.empty(),
                        minCostPerSquareFoot: minCostPerSquareFoot.minCostPerSquareFoot,
                        versionId: minCostPerSquareFoot.versionId,
                    });
                });

            const defaultStatesPersonal: MinCostPerSquareFootFriendlyViewSummary[] = minCostsPerSquareFoot
                .filter((minCostPerSquareFoot: MinCostPerSquareFootWithId) =>
                    minCostPerSquareFoot.broadLineOfBusinessCode === BroadLineOfBusiness.PERSONAL
                    && minCostPerSquareFoot.constructionTypeCode === null
                    && minCostPerSquareFoot.stateCode)
                .map((minCostPerSquareFoot: MinCostPerSquareFootWithId) => {
                    return new MinCostPerSquareFootFriendlyViewSummary({
                        id: minCostPerSquareFoot.id ?? 0,
                        broadLineOfBusiness: 'Personal',
                        state: minCostPerSquareFoot.stateCode ?? String.empty(),
                        constructionType: 'State Default',
                        minCostPerSquareFoot: minCostPerSquareFoot.minCostPerSquareFoot,
                        versionId: minCostPerSquareFoot.versionId,
                    });
                });

            const defaultConstructionTypesPersonal: MinCostPerSquareFootFriendlyViewSummary[] = minCostsPerSquareFoot
                .filter((minCostPerSquareFoot: MinCostPerSquareFootWithId) =>
                    minCostPerSquareFoot.broadLineOfBusinessCode === BroadLineOfBusiness.PERSONAL
                    && minCostPerSquareFoot.constructionTypeCode
                    && minCostPerSquareFoot.stateCode === null)
                .map((minCostPerSquareFoot: MinCostPerSquareFootWithId) => {
                    const match: PossibleConstructionType | undefined = possibleConstructionTypesPersonal
                        .find((possibleConstructionType) => possibleConstructionType.code === minCostPerSquareFoot.constructionTypeCode);
                    return new MinCostPerSquareFootFriendlyViewSummary({
                        id: minCostPerSquareFoot.id ?? 0,
                        broadLineOfBusiness: 'Personal',
                        state: 'All States',
                        constructionType: match?.displayName ?? String.empty(),
                        minCostPerSquareFoot: minCostPerSquareFoot.minCostPerSquareFoot,
                        versionId: minCostPerSquareFoot.versionId,
                    });
                });

            let minCostsPerSquareFootPersonalWithFriendlyNames: MinCostPerSquareFootFriendlyViewSummary[] =
                [
                    ...personalMinCosts,
                    ...defaultStatesPersonal,
                    ...defaultConstructionTypesPersonal,
                ];

            const defaultPersonal: MinCostPerSquareFootWithId | undefined = minCostsPerSquareFoot
                .find((x) => x.constructionTypeCode === null && x.stateCode === null && x.broadLineOfBusinessCode === BroadLineOfBusiness.PERSONAL);

            if (defaultPersonal !== undefined) {
                minCostsPerSquareFootPersonalWithFriendlyNames.push(
                    new MinCostPerSquareFootFriendlyViewSummary({
                        id: defaultPersonal.id ?? 0,
                        broadLineOfBusiness: 'Personal',
                        state: 'All States',
                        constructionType: 'Default',
                        minCostPerSquareFoot: defaultPersonal.minCostPerSquareFoot,
                        versionId: defaultPersonal.versionId,
                    })
                );
            }
            return minCostsPerSquareFootPersonalWithFriendlyNames;
        }
    );

const makeGetMinCostPerSquareFootFriendlyViewCommercialSelector = () => createSelector(
    [
        makeGetMinCostsPerSquareFootSelector(),
        makeGetPossibleConstructionTypesCommercialSelector(),
    ],
    (
        minCostsPerSquareFoot: MinCostPerSquareFootWithId[],
        possibleConstructionTypesCommercial: PossibleConstructionType[]
    ): MinCostPerSquareFootFriendlyViewSummary[] => {
        const commercialMinCosts: MinCostPerSquareFootFriendlyViewSummary[] = minCostsPerSquareFoot
            .filter((minCostPerSquareFoot: MinCostPerSquareFootWithId) =>
                minCostPerSquareFoot.broadLineOfBusinessCode === BroadLineOfBusiness.COMMERCIAL
                && minCostPerSquareFoot.constructionTypeCode
                && minCostPerSquareFoot.stateCode)
            .map((minCostPerSquareFoot: MinCostPerSquareFootWithId) => {
                const match: PossibleConstructionType | undefined = possibleConstructionTypesCommercial
                    .find((possibleConstructionType) => possibleConstructionType.code === minCostPerSquareFoot.constructionTypeCode);
                return new MinCostPerSquareFootFriendlyViewSummary({
                    id: minCostPerSquareFoot.id ?? 0,
                    broadLineOfBusiness: 'Commercial',
                    state: minCostPerSquareFoot.stateCode ?? String.empty(),
                    constructionType: match?.displayName ?? String.empty(),
                    minCostPerSquareFoot: minCostPerSquareFoot.minCostPerSquareFoot,
                    versionId: minCostPerSquareFoot.versionId,
                });
            });

        const defaultStatesCommercial: MinCostPerSquareFootFriendlyViewSummary[] = minCostsPerSquareFoot
                .filter((minCostPerSquareFoot: MinCostPerSquareFootWithId) =>
                    minCostPerSquareFoot.broadLineOfBusinessCode === BroadLineOfBusiness.COMMERCIAL
                    && minCostPerSquareFoot.constructionTypeCode === null
                    && minCostPerSquareFoot.stateCode)
                .map((minCostPerSquareFoot: MinCostPerSquareFootWithId) => {
                    return new MinCostPerSquareFootFriendlyViewSummary({
                        id: minCostPerSquareFoot.id ?? 0,
                        broadLineOfBusiness: 'Commercial',
                        state: minCostPerSquareFoot.stateCode ?? String.empty(),
                        constructionType: 'State Default',
                        minCostPerSquareFoot: minCostPerSquareFoot.minCostPerSquareFoot,
                        versionId: minCostPerSquareFoot.versionId,
                    });
                });

        const defaultConstructionTypesCommercial: MinCostPerSquareFootFriendlyViewSummary[] = minCostsPerSquareFoot
            .filter((minCostPerSquareFoot: MinCostPerSquareFootWithId) =>
                minCostPerSquareFoot.broadLineOfBusinessCode === BroadLineOfBusiness.COMMERCIAL
                && minCostPerSquareFoot.constructionTypeCode
                && minCostPerSquareFoot.stateCode === null)
            .map((minCostPerSquareFoot: MinCostPerSquareFootWithId) => {
                const match: PossibleConstructionType | undefined = possibleConstructionTypesCommercial
                    .find((possibleConstructionType) => possibleConstructionType.code === minCostPerSquareFoot.constructionTypeCode);
                return new MinCostPerSquareFootFriendlyViewSummary({
                    id: minCostPerSquareFoot.id ?? 0,
                    broadLineOfBusiness: 'Commercial',
                    state: 'All States',
                    constructionType: match?.displayName ?? String.empty(),
                    minCostPerSquareFoot: minCostPerSquareFoot.minCostPerSquareFoot,
                    versionId: minCostPerSquareFoot.versionId,
                });
            });

        let minCostsPerSquareFootCommercialWithFriendlyNames: MinCostPerSquareFootFriendlyViewSummary[] =
            [
                ...commercialMinCosts,
                ...defaultStatesCommercial,
                ...defaultConstructionTypesCommercial,
            ];

        const defaultCommercial: MinCostPerSquareFootWithId | undefined = minCostsPerSquareFoot
            .find((x) => x.constructionTypeCode === null && x.stateCode === null && x.broadLineOfBusinessCode === BroadLineOfBusiness.COMMERCIAL);

        if (defaultCommercial !== undefined) {
            minCostsPerSquareFootCommercialWithFriendlyNames.push(
                new MinCostPerSquareFootFriendlyViewSummary({
                    id: defaultCommercial.id ?? 0,
                    broadLineOfBusiness: 'Commercial',
                    state: 'All States',
                    constructionType: 'Default',
                    minCostPerSquareFoot: defaultCommercial.minCostPerSquareFoot,
                    versionId: defaultCommercial.versionId,
                })
            );
        }
        return minCostsPerSquareFootCommercialWithFriendlyNames;
    }
);

const makeGetMinCostPerSquareFootFriendlyViewSummariesSelector = () => createSelector(
    [
        makeGetMinCostPerSquareFootFriendlyViewPersonalSelector(),
        makeGetMinCostPerSquareFootFriendlyViewCommercialSelector(),
    ],
    (
        minCostsPerSquareFootPersonalWithFriendlyNames: MinCostPerSquareFootFriendlyViewSummary[],
        minCostsPerSquareFootCommercialWithFriendlyNames: MinCostPerSquareFootFriendlyViewSummary[]
    ): MinCostPerSquareFootFriendlyViewSummary[] => {
        return [
            ...minCostsPerSquareFootPersonalWithFriendlyNames,
            ...minCostsPerSquareFootCommercialWithFriendlyNames,
        ];
    }
);
//#endregion Friendly View Selectors

// Get Selected Min Cost Per Square Foot Id Selector.
const makeGetSelectedMinCostPerSquareFootIdSelector = () =>
    createSelector(
        [getMinCostPerSquareFootVersionState],
        (
            minCostPerSquareFootState:
                | MinCostPerSquareFootVersionState
                | undefined
        ): number | undefined => {
            return minCostPerSquareFootState?.selectedMinCostPerSquareFootId;
        }
    );

// Get Selected Min Cost Per Square Foot Selector.
const makeGetSelectedMinCostPerSquareFootSelector = () => createSelector(
    [
        makeGetSelectedMinCostPerSquareFootIdSelector(),
        makeGetMinCostsPerSquareFootSelector(),
    ],
    (
        selectedMinCostPerSquareFootId: number | undefined,
        minCostsPerSquareFoot: MinCostPerSquareFootWithId[]
    ): MinCostPerSquareFootWithId | undefined => {
        return selectedMinCostPerSquareFootId !== undefined
            ? minCostsPerSquareFoot.find((minCostPerSquareFoot: MinCostPerSquareFootWithId) => minCostPerSquareFoot.id === selectedMinCostPerSquareFootId)
            : undefined;
    }
);

// Are Min Costs Per Square Foot loaded Selector.
const makeAreMinCostsPerSquareFootLoadedSelector = () => createSelector(
        [getMinCostPerSquareFootVersionState],
        (minCostPerSquareFootState: MinCostPerSquareFootVersionState | undefined): boolean => {
            return minCostPerSquareFootState?.areMinCostsPerSquareFootLoaded ?? false;
        }
    );

export {
    makeGetMinCostsPerSquareFootSelector,
    makeGetMinCostPerSquareFootFriendlyViewSummariesSelector,
    makeGetSelectedMinCostPerSquareFootIdSelector,
    makeGetSelectedMinCostPerSquareFootSelector,
    makeAreMinCostsPerSquareFootLoadedSelector,
};